import * as React from "react";
import { useState } from "react";
import styled, { css } from "styled-components";
import { Badge, Label } from "components/bootstrap";
import { Icon } from "components/common";
const DEFAULT_COLLAPSED_COUNT = 3;
const Wrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.xs};
    flex-wrap: wrap;

    /* Fill the cell so max-width: 100% on chips resolves against the cell width. */
    width: 100%;
    min-width: 0;
  `
);
const ChipsContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacings.xs};
    min-width: 0;
  `
);
const HoverableLabel = styled(Label)(
  ({ theme }) => css`
    transition: background-color 0.1s ease-in-out;

    /* Override Label's flex label so the bare-string chip can be abbreviated with an ellipsis
       (overflow/text-overflow/white-space already come from Badge) instead of overflowing the cell. */
    .mantine-Badge-label {
      display: block;
    }

    /* Shrink as a flex item so the chip can truncate instead of leaking past the cell. */
    min-width: 0;
    max-width: 100%;

    /* Only clickable chips render as a <button> (via Label's own onClick prop) — restrict the
       hover/focus affordance to those so plain, non-clickable chips don't look interactive. */
    &:where(button):hover {
      background-color: ${theme.colors.gray[60]};
      cursor: pointer;
    }

    &:where(button):focus-visible {
      outline: 2px solid ${theme.colors.input.borderFocus};
      outline-offset: 2px;
      border-radius: 2px;
    }
  `
);
const ToggleButton = styled.button(
  ({ theme }) => css`
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;

    & * {
      cursor: pointer;
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.input.borderFocus};
      outline-offset: 2px;
      border-radius: 2px;
    }
  `
);
const ChipsCell = ({
  items,
  onItemClick = void 0,
  collapsedCount = DEFAULT_COLLAPSED_COUNT,
  truncate = true,
  emptyFallback = null,
  renderItemContent = void 0,
  itemLabel,
  itemTitle = void 0
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!(items == null ? void 0 : items.length)) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, emptyFallback);
  }
  const sorted = [...items].sort();
  const isTruncated = truncate && sorted.length > collapsedCount;
  const visible = !isExpanded && isTruncated ? sorted.slice(0, collapsedCount) : sorted;
  const hiddenCount = sorted.length - collapsedCount;
  const renderChip = (item) => {
    const content = renderItemContent ? renderItemContent(item) : item;
    if (!onItemClick) {
      return /* @__PURE__ */ React.createElement(HoverableLabel, { key: item, bsStyle: "default", title: renderItemContent ? void 0 : item }, content);
    }
    const filterLabel = `Filter by ${itemLabel} "${itemTitle ? itemTitle(item) : item}"`;
    return /* @__PURE__ */ React.createElement(
      HoverableLabel,
      {
        key: item,
        bsStyle: "default",
        onClick: (e) => {
          e.stopPropagation();
          onItemClick(item);
        },
        "aria-label": filterLabel,
        title: filterLabel
      },
      content
    );
  };
  return /* @__PURE__ */ React.createElement(Wrapper, null, /* @__PURE__ */ React.createElement(ChipsContainer, null, visible.map(renderChip)), isTruncated && /* @__PURE__ */ React.createElement(
    ToggleButton,
    {
      type: "button",
      "aria-label": isExpanded ? "Show fewer" : "Show all",
      title: isExpanded ? "Show fewer" : "Show all",
      onClick: (e) => {
        e.stopPropagation();
        setIsExpanded((v) => !v);
      }
    },
    /* @__PURE__ */ React.createElement(Badge, null, isExpanded ? /* @__PURE__ */ React.createElement(Icon, { name: "keyboard_arrow_up" }) : `+ ${hiddenCount}`)
  ));
};
export default ChipsCell;
